import React, { useEffect, useState } from "react";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {
  Box,
  Modal,
  Typography,
  Avatar,
  Stack,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompetitionVoteButton from "./CompetitionVoteButton";

import { contestantDisplayName } from "../../../utils/contestantDisplayName";
import {
  isCompetitionImageMedia,
  resolveCompetitionMediaPoster,
} from "../../../utils/competitionEventMedia";
import CompetitionMediaThumbnail from "./CompetitionMediaThumbnail";
import { SITE_BORDER, SITE_PAGE_BG } from "../../../utils/siteListTheme";

function displayName(video) {
  return contestantDisplayName(video);
}

export default function CompetitionVideoModal({
  open,
  onClose,
  video,
  votingEnabled = false,
  onVoted,
  competitionBanner = "",
}) {
  const [playbackStarted, setPlaybackStarted] = useState(false);

  useEffect(() => {
    if (open) setPlaybackStarted(false);
  }, [open, video?.id]);

  if (!video) return null;

  const name = displayName(video);
  const avatar =
    video.contestant?.profile_image || video.contestant?.user?.profile_img;
  const category = video.category || video.contestant?.category || "—";
  const eventName = video.event?.name || "Event";
  const voteCount = video.voteCount ?? video.vote_count ?? 0;
  const isImage = isCompetitionImageMedia(video);
  const poster = resolveCompetitionMediaPoster(video, competitionBanner);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="competition-video-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          outline: "none",
          width: "100%",
          maxWidth: 720,
          maxHeight: "92vh",
          overflow: "auto",
          borderRadius: 3,
          bgcolor: SITE_PAGE_BG,
          border: `1px solid ${SITE_BORDER}`,
          boxShadow: "0 24px 80px rgba(0,0,0,0.65)",
          backgroundImage:
            "radial-gradient(ellipse at 50% 0%, rgba(239,54,78,0.16) 0%, transparent 55%)",
          // Attractive scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(239,54,78,0.55) rgba(255,255,255,0.06)",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.04)",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "linear-gradient(180deg, #ef364e, #C9A66B)",
            borderRadius: 8,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 2.2, pt: 2, pb: 1.2 }}
        >
          <Box sx={{ pr: 1, minWidth: 0 }}>
            <Typography
              id="competition-video-modal"
              sx={{
                color: "#fff",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 800,
                fontSize: { xs: 18, sm: 22 },
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              {eventName}
            </Typography>
            <Stack direction="row" gap={0.8} flexWrap="wrap" mt={1}>
              <Chip
                size="small"
                label={category}
                sx={{
                  bgcolor: "rgba(246,231,193,0.1)",
                  color: "#F6E7C1",
                  fontWeight: 700,
                  border: "1px solid rgba(246,231,193,0.25)",
                }}
              />
            </Stack>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "rgba(255,255,255,0.7)",
              bgcolor: "rgba(255,255,255,0.06)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            mx: { xs: 1.5, sm: 2.2 },
            borderRadius: 2.5,
            overflow: "hidden",
            bgcolor: "#000",
            border: "1px solid rgba(255,255,255,0.08)",
            aspectRatio: "16 / 9",
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={video.video_url}
              alt={eventName}
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "contain",
              }}
            />
          ) : !playbackStarted ? (
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setPlaybackStarted(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setPlaybackStarted(true);
              }}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                cursor: "pointer",
                bgcolor: "#000",
              }}
            >
              <CompetitionMediaThumbnail
                item={video}
                objectFit="contain"
                competitionBanner={competitionBanner}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.25)",
                  transition: "background 0.2s ease",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "rgba(239,54,78,0.92)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 40, color: "#fff" }} />
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              component="video"
              key={`${video.id}-playing`}
              src={video.video_url}
              controls
              autoPlay
              playsInline
              poster={poster || undefined}
              preload="auto"
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "contain",
              }}
            />
          )}
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={2}
          sx={{ px: 2.2, py: 2.2 }}
        >
          <Stack direction="row" alignItems="center" gap={1.4} minWidth={0}>
            <Avatar
              src={avatar}
              sx={{
                width: 48,
                height: 48,
                border: "2px solid rgba(246,231,193,0.45)",
              }}
            >
              {name[0]}
            </Avatar>
            <Box minWidth={0}>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Uploaded by
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: 15,
                }}
              >
                {name}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            <CompetitionVoteButton
              videoId={video.id}
              count={voteCount}
              disabled={!votingEnabled}
              onVoted={onVoted}
            />
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}
